import { useEffect } from 'react';
import { create } from 'zustand';
import { apiFetch, useAuthStore } from './authStore';

/**
 * Shared admin org-scope: which organization an admin is currently viewing.
 *
 * Persists to the SAME localStorage key the Manager view already uses
 * (`g1.manager.selectedOrgId`), so the selection stays consistent across every
 * org-scoped screen via navigation — without refactoring Manager's internals.
 * The relay honors the selection via `?orgId=` (resolveOrgId, admin-only).
 * Admins only; non-admin managers always scope to their own org.
 */
const STORAGE_KEY = 'g1.manager.selectedOrgId';

export interface OrgOption {
  id: string;
  name: string;
}

interface OrgScopeState {
  selectedOrgId: string;
  orgs: OrgOption[];
  /** True only once a read succeeded. A failed read leaves it false with
   *  `orgsError` set — never an empty "no organizations" list. */
  orgsLoaded: boolean;
  /** The admin (user id) whose read produced `orgs` / `orgsError`. `null` =
   *  never read here, or seeded directly (tests). State stamped for someone
   *  else — a list or a failure — is never reused. */
  orgsFor: string | null;
  orgsError: string | null;
  setSelectedOrgId: (id: string) => void;
  /** Read the list unless it is already loaded (or loading) for the current
   *  admin. Never re-reads after a failure — that waits for `retryOrgs`. */
  ensureOrgs: () => Promise<void>;
  retryOrgs: () => Promise<void>;
}

function loadStored(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function currentAdmin(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

// The read in flight, and for whom. Only the latest read may land, and only
// while the admin it was started for is still the signed-in one.
let _inflight: { identity: string | null; seq: number; promise: Promise<void> } | null = null;
let _readSeq = 0;

export const useOrgScope = create<OrgScopeState>((set, get) => {
  const read = (): Promise<void> => {
    const identity = currentAdmin();
    const seq = ++_readSeq;
    const landed = () => seq === _readSeq && currentAdmin() === identity;
    const fail = (msg: string) => {
      if (!landed()) return;
      // Same admin: keep a list already shown (it stays marked by the error).
      set((s) => ({
        orgsLoaded: false,
        orgsError: msg,
        orgs: s.orgsFor === identity ? s.orgs : [],
        orgsFor: identity,
      }));
    };
    const promise = (async () => {
      try {
        const res = await apiFetch('/api/admin/orgs');
        const j = await res.json().catch(() => ({})) as { orgs?: unknown; error?: unknown };
        if (!res.ok) {
          return fail(typeof j.error === 'string' ? j.error : `Failed to load organizations (${res.status}).`);
        }
        if (!Array.isArray(j.orgs)) return fail('Unexpected response while loading organizations.');
        if (!landed()) return;
        const orgs = j.orgs.map((o: { id: string; name: string }) => ({ id: o.id, name: o.name }));
        set({ orgs, orgsLoaded: true, orgsFor: identity, orgsError: null });
      } catch {
        fail('Could not reach the server while loading organizations.');
      } finally {
        if (_inflight?.seq === seq) _inflight = null;
      }
    })();
    _inflight = { identity, seq, promise };
    return promise;
  };

  return {
    selectedOrgId: loadStored(),
    orgs: [],
    orgsLoaded: false,
    orgsFor: null,
    orgsError: null,
    setSelectedOrgId: (id) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      set({ selectedOrgId: id });
    },
    ensureOrgs: () => {
      const identity = currentAdmin();
      // Nobody signed in: nothing to read, and a read would leave an unowned
      // failure that the next admin would inherit.
      if (identity === null) return Promise.resolve();
      const s = get();
      const forMe = s.orgsFor === null || s.orgsFor === identity;
      if (forMe && (s.orgsLoaded || s.orgsError)) return Promise.resolve();
      if (_inflight?.identity === identity) return _inflight.promise;
      // Another admin's list must not stand in while this admin's is read.
      if (!forMe) set({ orgs: [], orgsLoaded: false, orgsFor: null, orgsError: null });
      return read();
    },
    retryOrgs: () => {
      if (currentAdmin() === null) return Promise.resolve();
      set({ orgsError: null });
      return read();
    },
  };
});

/** Drop the list on sign-out / identity replacement. Runs from authStore's
 *  deferred purge, possibly after the new admin's own read landed, so only a
 *  list (or read) belonging to someone else is dropped. */
export function resetOrgScopeOrgs(): void {
  const identity = currentAdmin();
  if (_inflight && _inflight.identity !== identity) {
    _inflight = null;
    _readSeq++;
  }
  const s = useOrgScope.getState();
  if (s.orgsFor !== null && s.orgsFor !== identity) {
    useOrgScope.setState({ orgs: [], orgsLoaded: false, orgsFor: null, orgsError: null });
  }
}

const NO_ORGS: OrgOption[] = [];

/** The admin org list as the signed-in admin may see it, read once per admin
 *  while `enabled`. Another admin's list or failure reads as not loaded, from
 *  the first render after a switch. `error` is set only after a failed read;
 *  `retry` is the only way to read again after one (no automatic re-reads). */
export function useAdminOrgs(enabled: boolean): {
  orgs: OrgOption[];
  loaded: boolean;
  error: string | null;
  retry: () => void;
} {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const orgs = useOrgScope((s) => s.orgs);
  const orgsLoaded = useOrgScope((s) => s.orgsLoaded);
  const orgsFor = useOrgScope((s) => s.orgsFor);
  const orgsError = useOrgScope((s) => s.orgsError);
  const ensureOrgs = useOrgScope((s) => s.ensureOrgs);
  const retryOrgs = useOrgScope((s) => s.retryOrgs);

  useEffect(() => {
    if (enabled) void ensureOrgs();
  }, [enabled, userId, ensureOrgs]);

  const mine = orgsFor === null || orgsFor === userId;
  return {
    orgs: mine ? orgs : NO_ORGS,
    loaded: mine && orgsLoaded,
    error: mine ? orgsError : null,
    retry: () => { void retryOrgs(); },
  };
}
